import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthState } from "../context/AuthProvider";
import {
  MdDashboard,
  MdListAlt,
  MdAttachMoney,
  MdAssessment,
  MdSettings,
  MdPowerSettingsNew,
  MdMenu,
  MdCurrencyRupee,
} from "react-icons/md";
import { FaUserCircle } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../translations";

const SidebarLayout = ({ children }) => {
  const { auth, setAuth } = AuthState();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Default closed on mobile
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [systemLogo, setSystemLogo] = useState(localStorage.getItem("systemLogo") || "");
  const [systemName, setSystemName] = useState(localStorage.getItem("systemName") || "");

  // Update system info when navigating (acts as a simple sync)
  React.useEffect(() => {
    setSystemLogo(localStorage.getItem("systemLogo") || "");
    setSystemName(localStorage.getItem("systemName") || "");
  }, [location.pathname]);

  const getInitials = (name = "") => {
    const cleanName = name.trim();
    if (!cleanName) return "?";
    const parts = cleanName.split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return cleanName.substring(0, 2).toUpperCase();
  };

  // Auto-close sidebar on mobile when route changes
  React.useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  // Desktop default open
  React.useEffect(() => {
    if (window.innerWidth >= 1024) {
      setIsSidebarOpen(true);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("auth");
    setAuth(null);
    navigate("/login");
  };

  const thoughts = t("thoughts") || [];
  const dailyThought = thoughts[Math.floor(Math.random() * thoughts.length)];

  const navItems = [

    ...(auth?.role === "Admin"
      ? [
        {
          name: t("sidebar.dashboard"),
          path: "/dashboard",
          icon: <MdDashboard className="w-5 h-5 mr-3" />,
        },
        {
          name: t("sidebar.dailyEntry"),
          path: "/daily-entry",
          icon: <MdListAlt className="w-5 h-5 mr-3" />,
        },
        {
          name: t("sidebar.pricing"),
          path: "/pricing",
          icon: <MdCurrencyRupee className="w-5 h-5 mr-3" />,
        },
        {
          name: t("sidebar.reports"),
          path: "/reports",
          icon: <MdAssessment className="w-5 h-5 mr-3" />,
        },
        {
          name: t("sidebar.settings"),
          path: "/settings",
          icon: <MdSettings className="w-5 h-5 mr-3" />,
        },
      ]
      : []),

    ...(auth?.role === "Staff"
      ? [
        {
          name: t("sidebar.dailyEntry"),
          path: "/daily-entry",
          icon: <MdListAlt className="w-5 h-5 mr-3" />,
        },
        {
          name: t("sidebar.pricing"),
          path: "/pricing",
          icon: <MdCurrencyRupee className="w-5 h-5 mr-3" />,
        },
        {
          name: t("sidebar.settings"),
          path: "/settings",
          icon: <MdSettings className="w-5 h-5 mr-3" />,
        },
      ]
      : []),

  ];

  return (
    <div className="flex flex-col h-screen bg-[#f8fafc] font-sans">
      {/* Top Header */}
      {/* <header className="h-16 bg-white shadow-sm flex items-center justify-between px-6 z-20 border-b border-gray-200 flex-shrink-0">
        
      </header> */}
      <header className="h-16 bg-white shadow-sm flex items-center justify-between px-6 z-20 border-b border-gray-200 flex-shrink-0">
        {/* LEFT SECTION */}
        <div className="flex items-center space-x-3">
          {/* LOGO */}
          {systemLogo ? (
            <img
              src={systemLogo}
              alt="Logo"
              className="w-8 h-8 rounded-lg object-cover shadow-sm transition-all duration-300"
            />
          ) : (
            <div className="bg-blue-600 p-1.5 rounded-lg shadow-sm transition-all duration-300">
              <MdCurrencyRupee className="w-5 h-5 text-white" />
            </div>
          )}
          {/* ANIMATED MENU BUTTON */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`relative flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-300 overflow-hidden
        ${isSidebarOpen
                ? "bg-blue-600 shadow-lg shadow-blue-200"
                : "bg-gray-100 hover:bg-blue-50"
              }`}
          >
            {/* HAMBURGER/X ANIMATION */}
            <div className="relative w-5 h-5 flex flex-col justify-between">
              {/* TOP LINE */}
              <span
                className={`block h-[2px] rounded-full transition-all duration-300
            ${isSidebarOpen
                    ? "w-5 bg-white rotate-45 translate-y-[7px]"
                    : "w-5 bg-gray-600"
                  }`}
              />
              {/* MIDDLE LINE */}
              <span
                className={`block h-[2px] rounded-full transition-all duration-300
            ${isSidebarOpen
                    ? "opacity-0"
                    : "w-4 bg-gray-600"
                  }`}
              />
              {/* BOTTOM LINE */}
              <span
                className={`block h-[2px] rounded-full transition-all duration-300
            ${isSidebarOpen
                    ? "w-5 bg-white -rotate-45 -translate-y-[7px]"
                    : "w-3 bg-gray-600"
                  }`}
              />
            </div>
          </button>
          {/* COMPANY NAME */}
          <h1 className="text-[18px] font-extrabold text-[#0f172a] tracking-tight uppercase transition-all duration-300">
            {systemName || t("sidebar.logo")}
          </h1>
        </div>
        {/* RIGHT SECTION */}
        <div className="flex items-center space-x-4 relative">
          {/* THOUGHT */}
          <div className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 px-4 py-1.5 rounded-full shadow-sm">
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">
              {t("sidebar.todaysThought")}
            </span>
            <span className="text-[12px] font-medium text-gray-700 italic">
              "{dailyThought}"
            </span>
          </div>
          {/* PROFILE */}
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none pl-2 border-l border-gray-200"
          >
            {auth?.profilePic ? (
              <img
                src={auth.profilePic}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover border border-gray-200"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold shadow-inner">
                {getInitials(auth?.name || "User")}
              </div>
            )}
            <div className="hidden sm:flex flex-col items-start leading-tight">
              <span className="font-bold text-sm text-gray-800">
                {auth?.name || "User"}
              </span>
              <span className="text-[10px] font-bold text-blue-600 uppercase tracking-tighter">
                {auth?.role || "Staff"}
              </span>
            </div>
            <FiChevronDown className="w-4 h-4" />
          </button>
          {/* DROPDOWN */}
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-xl py-2 border border-gray-100 z-50 overflow-hidden">
              <button
                onClick={() => {
                  setDropdownOpen(false);
                  navigate(`/settings?highlight=${auth?._id}`);
                }}
                className="flex items-center w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors space-x-2"
              >
                <FaUserCircle className="w-4 h-4" />
                <span className="font-medium">My Profile</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors space-x-2"
              >
                <MdPowerSettingsNew className="w-4 h-4" />
                <span className="font-medium">{t("sidebar.logout")}</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 overflow-hidden relative">
          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div
            className={`${isSidebarOpen ? "translate-x-0 w-64" : "-translate-x-full lg:translate-x-0 lg:w-20"
              } fixed lg:static inset-y-0 left-0 z-40 bg-[#0f172a] text-white transition-all duration-300 ease-in-out flex flex-col shadow-xl lg:shadow-none`}
          >
            <nav className="flex-1 overflow-y-auto py-6">
              <ul className="space-y-2 px-4">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.path}
                        className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 group ${isActive
                          ? "bg-blue-600 text-white shadow-lg shadow-blue-900/20"
                          : "text-gray-400 hover:bg-gray-800 hover:text-white"
                          }`}
                        title={!isSidebarOpen ? item.name : ""}
                      >
                        <div className={`transition-transform duration-200 group-hover:scale-110 ${isActive ? "text-white" : "text-gray-500 group-hover:text-blue-400"}`}>
                          {item.icon}
                        </div>
                        <span className={`${!isSidebarOpen && "lg:hidden"} font-bold text-sm ml-0 tracking-wide`}>{item.name}</span>
                        {isActive && isSidebarOpen && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white shadow-lg shadow-white/50"></div>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="px-4 pb-6 mt-auto">
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 text-gray-400 hover:text-rose-400 hover:bg-gray-800 rounded-xl transition-all duration-200 group"
                title={!isSidebarOpen ? t("sidebar.logout") : ""}
              >
                <div className="group-hover:rotate-12 transition-transform">
                  <MdPowerSettingsNew className="w-5 h-5 mr-3" />
                </div>
                <span className={`${!isSidebarOpen && "lg:hidden"} font-bold text-sm tracking-wide`}>{t("sidebar.logout")}</span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 md:p-6 bg-[#f8fafc]">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SidebarLayout;
